import type { Choice } from './choices'
interface ModelChoice extends Choice { resolved?: string; retired?: boolean }

/** Vendor IDs define families; numeric versions rank within them. Dates are
 * snapshots, and context suffixes remain distinct choices. Unknown/unversioned
 * names stay visible. On equal versions prefer a resolving alias, then vendor
 * order. No model or family names are maintained here (SUN-1477).
 */
function identity(model: ModelChoice) {
  const nativeId = 'modelId' in model && typeof model.modelId === 'string' ? model.modelId : model.id
  const id = model.resolved ?? model.id
  const program = 'selectionProgram' in model ? model.selectionProgram ?? 'standard' : 'standard'
  const match = /(?:^|[-_])(\d+(?:[.-]\d+)*)(?=$|[-_\[])/.exec(id)
  if (!match) return { family: String(program) + ':' + id, version: [] as number[], alias: nativeId !== id }
  const version = match[1]!.split(/[.-]/).filter((part) => part.length < 8).map(Number)
  const family = String(program) + ':' + id.slice(0, match.index) + '-' + id.slice(match.index + match[0].length)
  return { family, version, alias: nativeId !== id }
}

function compare(left: number[], right: number[]) {
  for (let n = 0; n < Math.max(left.length, right.length); n++) {
    const difference = (left[n] ?? 0) - (right[n] ?? 0)
    if (difference) return difference
  }
  return 0
}

/** The newest of the models by version; the first offered among equals, and a model without a version ranks below any with one. */
export function newestModel<T extends ModelChoice>(models: readonly T[]): T | undefined {
  let newest: T | undefined
  for (const model of models) if (!model.retired && (!newest || compare(identity(model).version, identity(newest).version) > 0)) newest = model
  return newest
}

export function modelGroups(models: readonly ModelChoice[]) {
  const current = new Map<string, ModelChoice>()
  for (const model of models) {
    if (model.retired) continue
    const candidate = identity(model)
    const held = current.get(candidate.family)
    const before = held && identity(held)
    if (!before || compare(candidate.version, before.version) > 0 ||
      (compare(candidate.version, before.version) === 0 && candidate.alias && !before.alias)) current.set(candidate.family, model)
  }
  const latest = new Set(current.values())
  return { latest: models.filter((model) => latest.has(model)), older: models.filter((model) => !latest.has(model)) }
}
